import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Logger } from '@services/logging/logger';

@Injectable({ providedIn: 'root' })
export class EnableEngageCookieService {

    private static readonly COOKIE_NAME = 'ivd_enable_engage';
    private static readonly ONE_YEAR_IN_SECONDS = 365 * 24 * 60 * 60;
    private static readonly SHARED_DOMAIN = '.invitedesk.com';

    public read(): boolean | undefined {
        const rawValue = this.readRawCookie(EnableEngageCookieService.COOKIE_NAME);
        if (rawValue === undefined) {
            return undefined;
        }

        if (rawValue !== 'true' && rawValue !== 'false') {
            Logger.warn(`EnableEngageCookieService.read: unexpected ${EnableEngageCookieService.COOKIE_NAME} value "${rawValue}", treating as absent`);
            return undefined;
        }

        return rawValue === 'true';
    }

    public write(enabled: boolean): void {
        const parts = [
            `${EnableEngageCookieService.COOKIE_NAME}=${enabled}`,
            'Path=/',
            `Max-Age=${EnableEngageCookieService.ONE_YEAR_IN_SECONDS}`,
            'SameSite=Lax',
        ];

        const domain = this.cookieDomain();
        if (domain) {
            parts.push(`Domain=${domain}`);
        }

        if (this.isSecureContext()) {
            parts.push('Secure');
        }

        document.cookie = parts.join('; ');
    }

    private cookieDomain(): string | undefined {
        if (this.isSharedInvitedeskHost()) {
            return EnableEngageCookieService.SHARED_DOMAIN;
        }

        return undefined;
    }

    private isSharedInvitedeskHost(): boolean {
        const engageHost = this.engageHost();
        if (!engageHost) {
            return false;
        }

        const sharedDomain = EnableEngageCookieService.SHARED_DOMAIN;
        const apexHost = sharedDomain.slice(1);
        return engageHost === apexHost || engageHost.endsWith(sharedDomain);
    }

    private engageHost(): string | undefined {
        try {
            return new URL(environment.engage.baseUrl).hostname;
        } catch {
            Logger.warn(`EnableEngageCookieService.engageHost: cannot parse engage baseUrl "${environment.engage.baseUrl}"`);
            return undefined;
        }
    }

    private isSecureContext(): boolean {
        return window.location.protocol === 'https:';
    }

    private readRawCookie(name: string): string | undefined {
        const cookies = document.cookie ? document.cookie.split('; ') : [];
        const prefix = `${name}=`;
        const match = cookies.find(cookie => cookie.startsWith(prefix));
        if (!match) {
            return undefined;
        }

        return decodeURIComponent(match.substring(prefix.length));
    }
}
